import { z } from "zod";

export const DecorationRaritySchema = z.enum(["common", "rare", "epic"]);
export type DecorationRarity = z.infer<typeof DecorationRaritySchema>;

export const DecorationSourceSchema = z.enum(["chest", "event", "shop", "scrip"]);

/**
 * A placeable decoration: one shipped model, a footprint in base cells, and what standing it gives
 * the colony (D70, levelled in D93).
 *
 * A player said twelve copies of the same thing is nobody's idea of decorating. A kind stands once
 * and is raised with further copies instead, which is why `morale` is what one level is worth and
 * `upgrade` says how many copies each further level costs — a level four statue is a real thing to
 * own rather than four statues in a row.
 */
export const DecorationDefSchema = z.object({
  id: z.string().regex(/^deco\./),
  name: z.object({ de: z.string(), en: z.string() }),
  /** The inventory item that is consumed on placement and returned on removal (the garden has none to consume: it is bought). */
  itemId: z.string().regex(/^itm\.deco_/),
  model: z.string().regex(/^prop\./),
  footprint: z.object({ w: z.number().int().min(1).max(3), h: z.number().int().min(1).max(3) }),
  /** Standing per level; the colony's morale is the sum over everything standing. */
  morale: z.number().int().min(0).max(5),
  /** Copies needed for the step to level 2, 3, 4 … The length is the ceiling. */
  upgrade: z.array(z.number().int().min(1)).min(1),
  rarity: DecorationRaritySchema,
  source: z.array(DecorationSourceSchema).min(1),
});
export type DecorationDef = z.infer<typeof DecorationDefSchema>;

/** The highest level a kind can reach: one to stand it up, then one step per upgrade cost. */
export function decorationMaxLevel(def: DecorationDef): number {
  return def.upgrade.length + 1;
}

/** What the next level costs in copies, or null when it is already as high as it goes. */
export function decorationUpgradeCost(def: DecorationDef, level: number): number | null {
  return def.upgrade[level - 1] ?? null;
}

/** Everything ever put into a standing decoration, so storing it returns exactly that. */
export function decorationInvested(def: DecorationDef, level: number): number {
  return 1 + def.upgrade.slice(0, level - 1).reduce((sum, cost) => sum + cost, 0);
}
